import type { Location } from 'react-router-dom';
import { getEntityErrorPageUrl } from 'utils/url';
import async from '../components/Async';
import {
  TEAM_PROFILE_PATH_DEEP_LINK,
  REPORTS_TABLE_TEAMS_PATH_DEEP_LINK,
  HOME_PATH,
} from '../constants/paths';
import { getReportTeamsPageDeepLinkUrl } from '../utils/url';
import { shouldHaveSharedRootClass } from '../utils/routeMatching';
import * as constants from '../constants';
import {
  Briefcase,
  Home,
  Layout,
  Users,
  Trash2,
  DollarSign,
  BarChart2,
  FileText,
} from 'react-feather';
import { PricingCalculatorPage } from 'RetoolModule/components/PricingCalculatorPage';

// All pages that rely on 3rd party components (other than Material-UI) are
// loaded asynchronously, to keep the initial JS bundle to a minimum size

// Guards
import AuthGuard from '../components/AuthGuard';

// Auth components
import SignIn from '../pages/auth/SignIn';
import SignUp from '../pages/auth/SignUp';
import Page404 from '../pages/auth/Page404';
import Page500 from '../pages/auth/Page500';

// Custom components
const HomePage = async(() => import('../pages/HomePage'));
const Teams = async(() => import('ReportsModule/TeamsOverviewPage'));
const DataDeletionReviewPage = async(() => import('TeamModule/components/DataDeletionReviewPage'));
const TeamsV2Page = async(() => import('TeamModule/components/TeamsV2Page'));
const MembersPage = async(() => import('TeamModule/components/MembersPage'));
const Profile = async(() => import('ProfileModule/TeamProfilePage'));
const ActivityLogPage = async(() => import('ActivityLogModule/components/ActivityLogPage'));
const TeamIntegration = async(() => import('IntegrationModule/Team/TeamsIntegrationPage'));
const PendingEntity = async(() => import('IntegrationModule/PendingEntity/PendingEntityTablePage'));
const Logs = async(() => import('IntegrationModule/Logs/ApiRequestLogsPage'));
const IntegrationHealth = async(() => import('IntegrationModule/Health/IntegrationHealthPage'));
const EntityError = async(() => import('IntegrationModule/Logs/EntityError/EntityErrorContainer'));
// Commented out: Template files that were removed
// const Editors = async(() => import("../pages/forms/Editors"));
// const Formik = async(() => import("../pages/forms/Formik"));
// Maps components
// const GoogleMaps = async(() => import("../pages/maps/GoogleMaps"));
// const VectorMaps = async(() => import("../pages/maps/VectorMaps"));

// Documentation (commented out - these pages don't exist)
// import Welcome from "../pages/docs/Welcome";
// import GettingStarted from "../pages/docs/GettingStarted";
// import EnvironmentVariables from "../pages/docs/EnvironmentVariables";

// If you need to add route without sidebar, add it here
export const pagesRoutes = {
  id: 'Pages',
  path: '/pages',
  icon: <Layout />,
  children: [
    {
      path: TEAM_PROFILE_PATH_DEEP_LINK,
      name: 'Profile',
      component: Profile,
      guard: AuthGuard,
    },
    {
      path: `${constants.REPORTS_PENDING_ENTITY_PATH}/targetServiceId/:targetServiceId/teamId/:teamId`,
      name: 'Pending Entity',
      component: PendingEntity,
      guard: AuthGuard,
    },
    {
      path: `${constants.LOGS_PATH}/:mosaicTeamId`,
      component: Logs,
      guard: AuthGuard,
    },
    {
      path: constants.REPORTS_INTEGRATION_DETAILS_PATH_DEEP_LINK,
      name: 'Team-specific Integration',
      component: TeamIntegration,
      guard: AuthGuard,
    },
    {
      path: constants.INTEGRATION_HEALTH_PATH_DEEP_LINK,
      name: 'Integration Health',
      component: IntegrationHealth,
      guard: AuthGuard,
    },
    {
      path: getEntityErrorPageUrl(),
      component: EntityError,
      guard: AuthGuard,
    },
  ],
  component: null,
};

export const authRoutes = {
  id: 'Auth',
  path: '/auth',
  icon: <Users />,
  children: [
    {
      path: '/auth/sign-in',
      name: 'Sign In',
      component: SignIn,
    },
    {
      path: '/auth/sign-up',
      name: 'Sign Up',
      component: SignUp,
    },
    {
      path: '/auth/404',
      name: '404 Page',
      component: Page404,
    },
    {
      path: '/auth/500',
      name: '500 Page',
      component: Page500,
    },
  ],
  component: null,
};

/* ------------------------------ Routes in Use ----------------------------- */

export const homeRoutes = {
  id: 'Home',
  icon: <Home />,
  prod: true,
  path: HOME_PATH,
  component: HomePage,
};

export const teamRoutes = {
  id: 'Reports',
  icon: <BarChart2 />,
  prod: true,
  path: REPORTS_TABLE_TEAMS_PATH_DEEP_LINK,
  component: Teams,
  makeSidebarPath: () => getReportTeamsPageDeepLinkUrl({}),
  makeClassnames: (location: Location) => {
    // Use centralized route matching logic
    return shouldHaveSharedRootClass(location.pathname) ? 'has-shared-root-url' : undefined;
  },
};

// Note: Integration routes are dynamically generated in useRoutes.tsx
// based on agentDomains data, not statically defined here.
// See: src/routes/useRoutes.tsx integrationRoutes

export const dataDeletionRoutes = {
  id: 'Teams Data Deletion Review',
  icon: <Trash2 />,
  prod: true,
  path: '/data-deletion-review',
  component: DataDeletionReviewPage,
};

export const teamV2Routes = {
  id: 'Teams Management',
  icon: <Briefcase />,
  prod: true,
  path: constants.REPORTS_TABLE_TEAMS_V2_PATH,
  component: TeamsV2Page,
};

export const membersRoutes = {
  id: 'Members',
  icon: <Users />,
  prod: true,
  path: constants.REPORTS_TABLE_MEMBERS_PATH,
  component: MembersPage,
};

export const PricingToolRoutes = {
  id: 'Pricing Calculator',
  icon: <DollarSign />,
  prod: true,
  path: constants.PRICING_CALCULATOR_PATH,
  component: PricingCalculatorPage,
};

export const activityLogRoutes = {
  id: 'Activity Log',
  icon: <FileText />,
  prod: true,
  path: constants.ACTIVITY_LOG_PATH,
  component: ActivityLogPage,
};
